import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  SxProps,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import UserAnalyticsTable from "./UserAnalyticsTable";
import ViewRecipientsDialog, { loaderSx } from "./ViewRecipientsDialog";
import { getDistributionForwards } from "../api/requests/users";
import { useParams } from "react-router-dom";
import {
  sectionDescriptionSx,
  sectionTitleSx,
  tabsSx,
  tabSx,
} from "./UserAnalytics";
import { formatFullDateTime } from "../utils/utils";
import { PaginationType } from "./Groups";
import { textSx } from "./MandatoryReplies";
import { colors } from "../utils/constants";
import { viewReciepientStyles } from "./MessageStatsTable";

interface forwardStatsType {
  id?: number;
  messageText: string;
  forwardedDate: string;
  forwardedMessageId: number;
  forwaredBy: string;
  isGroup: boolean;
  reach: number;
  recipient: string;
}
const DistributionAnalyticsTable = () => {
  const { userId } = useParams();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [forwardStats, setForwardStats] = useState<forwardStatsType[]>([]);
  const [selectedRow, setSelectedRow] = useState<{
    id: number;
    gridSection: string;
  } | null>(null);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 5,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);

  const viewDetailsHandler = (id: number, gridSection: string) => {
    setSelectedRow({ id, gridSection });
  };

  const closeDialog = () => {
    setSelectedRow(null);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const columns: GridColDef[] = [
    {
      field: "messageText",
      headerName: "Text",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography fontSize="14px" sx={textSx}>
            {params.value || "--"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <Typography fontSize="14px">{params.value}</Typography>
      ),
    },
    {
      field: "receivedFrom",
      headerName: "Received From",
      flex: 1,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <Typography fontSize="14px">{params.value}</Typography>
      ),
    },
    {
      field: "recipient",
      headerName: "Recipient",
      flex: 1,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <Typography fontSize="14px">
          {params.value}{" "}
          <Box component={"span"} sx={groupTextSx}>
            {params.row.isGroup ? "group" : "individual"}
          </Box>
        </Typography>
      ),
    },
    {
      field: "reach",
      headerName: "Reach",
      flex: 1,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <Typography fontSize="14px">{params.value}</Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <Typography
          sx={viewReciepientStyles}
          onClick={() => viewDetailsHandler(params.row.id, "Forwards")}
        >
          View Recipients
        </Typography>
      ),
    },
  ];

  const fetchDistributionForwards = async () => {
    setLoading(true);
    try {
      const response =
        userId &&
        (await getDistributionForwards({
          userId,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }));
      if (response && response.userMessageDistributionForwards) {
        const updatedResponse = response.userMessageDistributionForwards.map(
          (item: forwardStatsType) => ({
            ...item,
            id: item.forwardedMessageId,
            date: formatFullDateTime(item.forwardedDate),
          })
        );
        setForwardStats(updatedResponse);
        setTotalRowsCount(response.totalCount);
      } else {
        setForwardStats([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch distribution forwards data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistributionForwards();
  }, [paginationModel]);

  return (
    <Box sx={analyticsOuterBoxStyles}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={sectionTitleSx}>Message Distribution</Typography>
        <Typography sx={sectionDescriptionSx}>
          Analysis of forwards and shares
        </Typography>
        {/* To do for later */}
        {/* <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={tabsSx}
          TabIndicatorProps={{
            style: { display: 'none' },
          }}
        >
          <Tab label='Forwards' sx={tabSx} />
        </Tabs> */}
      </Box>
      {/* Forwards */}
      {tabIndex === 0 && (
        <Box sx={{ height: "100%" }}>
          {loading ? (
            <Box sx={loadingBoxStyles}>
              <CircularProgress size={60} thickness={6} />
            </Box>
          ) : forwardStats.length > 0 ? (
            <UserAnalyticsTable
              rows={forwardStats}
              columns={columns}
              setPaginationModel={setPaginationModel}
              paginationModel={paginationModel}
              totalRowsCount={totalRowsCount}
            />
          ) : (
            <Box sx={loaderSx}>
              <Typography variant="body2" color="textSecondary">
                No data found.
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {selectedRow && (
        <ViewRecipientsDialog
          open={!!selectedRow}
          handleClose={closeDialog}
          id={selectedRow.id}
          title="Forward Recipients"
          description="List of all recipients and their interaction status"
        />
      )}
    </Box>
  );
};

export default DistributionAnalyticsTable;

export const analyticsOuterBoxStyles: SxProps = {
  flexGrow: 1,
  padding: 2,
  borderRadius: "6px",
};

const loadingBoxStyles: SxProps = {
  display: "flex",
  justifyContent: "center",
};

const groupTextSx: SxProps = {
  border: "1px solid #dedede",
  borderRadius: "10px",
  marginLeft: "10px",
  padding: "4px 8px",
  color: "#2f2f2f",
  fontSize: "12px",
};
